import { Injectable, inject } from "@angular/core";
import { BehaviorSubject, Observable, of } from "rxjs";
import { map } from "rxjs/operators";
import { CookieService } from "ngx-cookie-service";

import { decodeJwtPayload } from "@/app/core/helpers/jwt";
import { AuthenticationService } from "@/app/core/service/auth.service";
import { HttpClientService } from "./http-client/http-client.service";
import { BusinessProfile } from "./company-management-services.service";

export interface UserCompany {
  id: number;
  designacao: string;
  email: string;
  telefone: string;
  descricao: string;
  funcao: string;
  eprimeiro: boolean;
  createdAt: string;
}

const ACTIVE_COMPANY_KEY = "_RIZZ_ACTIVE_COMPANY_KEY_";

@Injectable({
  providedIn: "root",
})
export class CompanyContextService {
  private cookieService = inject(CookieService);
  private httpClientService = inject(HttpClientService);
  private authenticationService = inject(AuthenticationService);

  private activeCompanySubject = new BehaviorSubject<number | null>(
    this.readActiveCompanyId()
  );

  public readonly activeCompanyId$ = this.activeCompanySubject.asObservable();

  public get activeCompanyId(): number | null {
    return this.activeCompanySubject.value;
  }

  public get currentUserId(): number | null {
    const token = this.authenticationService.session;
    if (!token) return null;

    const payload = decodeJwtPayload<{ subject: number }>(token);
    return payload?.subject ?? null;
  }

  /**
   * The companies this user can manage. Support/admin manage any company from within
   * this same merchant app (just with no FuncoesUsuario link to each one) — for them
   * this returns every company instead of only ones they're literally staff of.
   */
  public getCompanies(): Observable<UserCompany[]> {
    if (this.authenticationService.isSupport || this.authenticationService.isAdmin) {
      const roleLabel = this.authenticationService.isAdmin ? 'Admin' : 'Suporte';
      return this.httpClientService._getRequest<BusinessProfile[]>('/empresa/admin/todas').pipe(
        map((companies) => companies.map((c): UserCompany => ({
          id: Number(c.id),
          designacao: c.designacao,
          email: c.email,
          telefone: c.telefone,
          descricao: c.descricao ?? '',
          funcao: roleLabel,
          eprimeiro: false,
          createdAt: (c as any).createdAt,
        }))),
      );
    }

    const userId = this.currentUserId;
    if (!userId) return of([]);

    return this.httpClientService._getRequest<UserCompany[]>(
      `/empresa/viausuario/${userId}`
    );
  }

  public selectCompany(companyId: number): void {
    this.cookieService.set(ACTIVE_COMPANY_KEY, String(companyId), undefined, "/");
    this.activeCompanySubject.next(companyId);
  }

  public clearActiveCompany(): void {
    this.cookieService.delete(ACTIVE_COMPANY_KEY, "/");
    this.activeCompanySubject.next(null);
  }

  private readActiveCompanyId(): number | null {
    const value = this.cookieService.get(ACTIVE_COMPANY_KEY);
    return value ? Number(value) : null;
  }
}
